import { throttle } from "lodash-es";
import { useCallback, useEffect, useId, useState } from "react";
import { ScreenReader } from "../../../components";
import styles from "./SliderInput.module.css";

export interface SliderProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "onChange" | "max"
  > {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  hideLabel?: boolean;
  description?: string;
  // The total number of tokens that can be visually represented
  absoluteMax: number;
  // The total number of tokens that can be allocated to this slider,
  // which may be less than absoluteMax if some tokens are allocated elsewhere
  constrainedMax: number;
}

export const SliderInput = ({
  onChange,
  label,
  hideLabel = false,
  description,
  className,
  absoluteMax,
  constrainedMax,
  ...props
}: SliderProps) => {
  const min = Number(props.min || 0);
  const max = Number(absoluteMax || 100);
  const step = Number(props.step || 1);
  const id = useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;

  const [value, setValue] = useState(
    typeof props.value === "number" ? props.value : (min + max) / 2,
  );

  useEffect(() => {
    if (typeof props.value === "number") {
      setValue(() => {
        return props.value as number;
      });
    }
  }, [props.value]);

  const handleChange = useCallback(
    throttle((e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target) {
        return;
      }
      const newValue = Number(e.target.value);
      setValue(newValue);

      if (onChange) {
        onChange(e);
      }
    }, 1000 / 60),
    [],
  );

  const usedValue = typeof props.value === "number" ? props.value : value;
  const actualPercent = ((usedValue - min) / (max - min)) * 100;

  return (
    <div className={styles["slider-container"]}>
      {label && (
        <label
          htmlFor={id}
          id={labelId}
          className={hideLabel ? styles["visually-hidden"] : styles.label}
        >
          {label}
        </label>
      )}

      <input
        {...props}
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={usedValue}
        onChange={handleChange}
        className={`${styles.slider} ${className || ""}`}
        style={
          {
            "--slider-percent": `${actualPercent}%`,
          } as React.CSSProperties
        }
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={usedValue}
        aria-valuetext={
          constrainedMax < max
            ? `${usedValue} out of ${constrainedMax} (${max - constrainedMax} allocated elsewhere)`
            : `${usedValue} out of ${max}`
        }
      />

      {description && (
        <ScreenReader id={descriptionId}>{description}</ScreenReader>
      )}
    </div>
  );
};
